import React, { ReactElement } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util';
import PdfViewer from './Pdf/PdfViewer';
import { remCalc } from '../../util/style-util';
import { MimeType } from '../../graph/schema-generated-types-educator';

const altT = (key) => translate('alt', key);

type Props = {
  files: any[] | undefined | null;
  fitToViewport?: boolean;
};
const FileViewer = ({ files, fitToViewport }: Props): ReactElement => {
  const classes = useStyles();

  return (
    <Box
      className={`${classes.wrapperClass} ${
        fitToViewport ? classes.fitToViewport : classes.compactPdf
      }`}
    >
      <Box style={{ flex: 1 }}>
        {files?.map((file) => {
          const fileUrl = file?.signedUrl ?? URL.createObjectURL(file);
          const isPdf =
            fileUrl?.endsWith('pdf') ||
            file?.mimeType === MimeType.PDF ||
            file?.type === 'application/pdf';
          return isPdf ? (
            <PdfViewer url={fileUrl} />
          ) : (
            <img src={fileUrl} alt={altT('previewSubmission')} className={classes.image} />
          );
        })}
      </Box>
    </Box>
  );
};

export default FileViewer;

const MAX_WIDTH = '90vw';
// BaseModalTitle is 60px tall plus a 1px divider
const MAX_CONTENT_HEIGHT = 'calc(90vh - 61px)';

export const fileViewerModalStyles = {
  getPaperStyles: (defaults: React.CSSProperties): React.CSSProperties => ({
    ...defaults,
    width: 'fit-content',
    maxWidth: MAX_WIDTH,
    maxHeight: '90vh',
  }),
  getContainerStyles: (defaults: React.CSSProperties): React.CSSProperties => ({
    ...defaults,
    maxWidth: MAX_WIDTH,
    maxHeight: MAX_CONTENT_HEIGHT,
    overflow: 'auto',
  }),
};

const useStyles = makeStyles(() => {
  return {
    wrapperClass: {
      width: 'fit-content',
      height: 'fit-content',
      maxWidth: '100vw',
      maxHeight: '100vh',
      boxSizing: 'border-box',
      // make enough space for the close icon
      marginTop: remCalc(10),
    },
    image: {
      borderRadius: remCalc(20),
      maxWidth: '100%',
      maxHeight: remCalc(350),
    },
    // leaves room for surrounding content such as accept/reject buttons
    compactPdf: {
      '& .react-pdf__Document': {
        minWidth: 0,
      },
      '& canvas': {
        width: 'auto !important',
        height: 'auto !important',
        maxWidth: '80vw',
        maxHeight: '60vh',
      },
      '& .react-pdf__Page__textContent, & .react-pdf__Page__annotations': {
        display: 'none',
      },
    },
    fitToViewport: {
      maxWidth: MAX_WIDTH,
      maxHeight: 'none',
      '& .react-pdf__Document': {
        minWidth: 0,
        maxWidth: MAX_WIDTH,
      },
      '& canvas': {
        maxWidth: `${MAX_WIDTH} !important`,
        height: 'auto !important',
      },
      '& img': {
        display: 'block',
        margin: '0 auto',
        width: 'auto',
        height: 'auto',
        maxWidth: MAX_WIDTH,
        maxHeight: MAX_CONTENT_HEIGHT,
        objectFit: 'contain',
      },
    },
  };
});
